/**
 * EraMediumFrame — gives any child (image, citation, snippet) the era's
 * physical-medium frame treatment. Stone gets chiseled border + faint relief;
 * vellum gets illuminated corners; CRT gets phosphor scanlines; web stays clean.
 *
 * Methodology: the medium IS the methodology. Images and citations carry
 * the same era language as text. A photograph from 1925 sits in a typewritten
 * paper backing. A primary source from 642 CE sits on a vellum mount. The
 * user reads the artifact and sees what kind of artifact it is at the same time.
 */
import { getEraMedium } from '../lib/eraMedia';

const EDGE_SVG = {
  'chiseled': null,           // handled via clip-path/box-shadow
  'rough-stone': null,
  'curled-scroll': null,      // shadow only
  'deckled': null,            // ::before via CSS would need stylesheet — use inline glow instead
  'aged': null,
  'columned': null,
  'platen-roll': null,
  'memo': null,
  'scanlines': null,
  'clean': null,
};

function corners(eraId, accent) {
  // Illuminated-manuscript corner accents — only for vellum/parchment eras
  if (eraId !== 'era3' && eraId !== 'era4') return null;
  const stroke = accent || '#C8A951';
  return (
    <svg
      aria-hidden="true"
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none', width: '100%', height: '100%' }}
      preserveAspectRatio="none"
      viewBox="0 0 100 100"
    >
      <path d="M 1 6 L 1 1 L 6 1" stroke={stroke} strokeWidth="0.4" fill="none" opacity="0.55" />
      <path d="M 99 6 L 99 1 L 94 1" stroke={stroke} strokeWidth="0.4" fill="none" opacity="0.55" />
      <path d="M 1 94 L 1 99 L 6 99" stroke={stroke} strokeWidth="0.4" fill="none" opacity="0.55" />
      <path d="M 99 94 L 99 99 L 94 99" stroke={stroke} strokeWidth="0.4" fill="none" opacity="0.55" />
    </svg>
  );
}

function frameStyleFor(eraId) {
  const m = getEraMedium(eraId);
  switch (m.edgeStyle) {
    case 'chiseled':
      return {
        boxShadow: 'inset 0 0 0 2px rgba(60,40,20,0.55), inset 8px 8px 22px rgba(0,0,0,0.22), inset -8px -8px 22px rgba(255,235,200,0.06), 0 4px 16px rgba(0,0,0,0.4)',
        clipPath: 'polygon(2% 0, 98% 1%, 100% 5%, 99% 95%, 97% 100%, 3% 99%, 1% 95%, 0% 5%)',
        background: m.background,
      };
    case 'curled-scroll':
      return {
        boxShadow: 'inset 0 12px 14px -8px rgba(120,90,40,0.4), inset 0 -12px 14px -8px rgba(120,90,40,0.4), 0 6px 16px rgba(0,0,0,0.3)',
        borderRadius: '4px',
        background: m.background,
      };
    case 'deckled':
      return {
        boxShadow: '0 0 0 1px rgba(180,140,90,0.3), inset 0 0 36px rgba(140,100,60,0.20), 0 4px 12px rgba(0,0,0,0.25)',
        background: m.background,
      };
    case 'aged':
      return {
        boxShadow: 'inset 0 0 32px rgba(120,90,50,0.16), 0 2px 8px rgba(0,0,0,0.18)',
        background: m.background,
      };
    case 'columned':
      return {
        boxShadow: 'inset 0 0 0 1px rgba(80,60,30,0.22), 0 2px 6px rgba(0,0,0,0.2)',
        background: m.background,
      };
    case 'platen-roll':
      return {
        boxShadow: 'inset 0 8px 10px -8px rgba(60,40,20,0.45), inset 0 -8px 10px -8px rgba(60,40,20,0.45), 0 2px 8px rgba(0,0,0,0.2)',
        background: m.background,
      };
    case 'memo':
      return {
        boxShadow: 'inset 0 0 0 1px rgba(20,40,80,0.18), 0 2px 6px rgba(0,0,0,0.18)',
        background: m.background,
      };
    case 'scanlines':
      return {
        boxShadow: 'inset 0 0 26px rgba(0,255,100,0.10), 0 0 1px rgba(0,255,100,0.5), 0 0 24px rgba(0,255,100,0.08)',
        background: m.background,
      };
    case 'rough-stone':
      return {
        boxShadow: 'inset 0 0 0 1px rgba(232,181,133,0.22), inset 0 0 36px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.3)',
        background: m.background,
      };
    case 'clean':
    default:
      return {
        boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 2px 8px rgba(0,0,0,0.18)',
      };
  }
}

export default function EraMediumFrame({ eraId, children, className = '', style = {} }) {
  const fs = frameStyleFor(eraId);
  const m = getEraMedium(eraId);
  return (
    <div
      className={`era-medium-frame ${className}`}
      data-era-frame={m.surface}
      style={{
        position: 'relative',
        overflow: 'hidden',
        ...fs,
        ...style,
      }}
    >
      {children}
      {corners(eraId, m.dropCap?.accent)}
    </div>
  );
}
